import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
/**
 * @page PageSettingsPage
 * @description Page settings — customize site name and sidebar nav icons
 * Route: /admin/page-settings
 */
import { Fragment, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { buildNavItems, buildSidebarItems } from '../../components/layout/navItems';
import { BrandIcon, ICON_CHOICES } from '../../components/layout/BrandIcon';

type NavIconMap = Record<string, string>;

const ACTION =
  'relative inline-flex min-h-11 items-center justify-center gap-1 px-4 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50';
const PRIMARY = `${ACTION} bg-primary text-primary-content hover:bg-primary/90`;
const OUTLINE = `${ACTION} border border-base-300 text-base-content hover:bg-base-300`;

/** One sidebar entry: its current icon, name and an inline picker opened on demand. */
export function IconRow({
  itemKey,
  defaultIcon,
  label,
  icons,
  open,
  onToggle,
  onUpdate,
  indent,
}: {
  itemKey: string;
  defaultIcon: string;
  label: string;
  icons: NavIconMap;
  open: boolean;
  onToggle: () => void;
  onUpdate: (key: string, val: string) => void;
  indent?: boolean;
}) {
  const { t } = useTranslation('admin');
  const current = icons[itemKey] ?? '';
  const pickerId = `icon-picker-${itemKey}`;
  // A saved symbol outside the named set (e.g. an emoji) stays selectable until replaced.
  const custom = current && !(ICON_CHOICES as readonly string[]).includes(current) ? current : '';
  const options: { value: string; icon: string; name: string }[] = [
    { value: '', icon: defaultIcon, name: t('pageSettingsDefaultIcon') },
    ...(custom ? [{ value: custom, icon: custom, name: t('pageSettingsCustomIcon') }] : []),
    ...ICON_CHOICES.map((value) => ({ value, icon: value, name: t(`iconName_${value}`) })),
  ];

  return (
    <li className={indent ? 'ml-4 sm:ml-8' : ''}>
      <div className="flex min-h-14 items-center gap-3 border-b border-base-300 px-2 py-1">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center text-base-content">
          <BrandIcon value={current || defaultIcon} size={22} />
        </span>
        <span className={`flex-1 text-sm text-base-content ${indent ? '' : 'font-semibold'}`}>
          {label}
        </span>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={pickerId}
          onClick={onToggle}
          className={OUTLINE}
        >
          {t('pageSettingsChangeIcon')}
          <span className="sr-only">{label}</span>
        </button>
      </div>
      {open && (
        <fieldset id={pickerId} className="relative border-b border-base-300 bg-base-200 p-3">
          <legend className="sr-only">{t('pageSettingsChooseFor', { label })}</legend>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(2.75rem,1fr))] gap-1">
            {options.map((option) => (
              <label
                key={option.value || 'default'}
                title={option.name}
                className={`relative flex h-11 min-w-11 cursor-pointer items-center justify-center border text-base-content transition-colors has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-primary ${
                  current === option.value
                    ? 'border-base-content bg-base-300'
                    : 'border-transparent hover:bg-base-300'
                }`}
              >
                <input
                  type="radio"
                  name={pickerId}
                  value={option.value}
                  checked={current === option.value}
                  onChange={() => onUpdate(itemKey, option.value)}
                  className="sr-only"
                />
                {option.value ? (
                  <>
                    <span className="sr-only">{option.name}</span>
                    <BrandIcon value={option.icon} size={22} />
                  </>
                ) : (
                  <span className="px-2 text-sm font-medium">{option.name}</span>
                )}
              </label>
            ))}
          </div>
        </fieldset>
      )}
    </li>
  );
}

export default function PageSettingsPage() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const navItems = buildSidebarItems(buildNavItems(tc), tc);
  const qc = useQueryClient();

  const [saveError, setSaveError] = useState('');
  const [openKey, setOpenKey] = useState<string | null>(null);

  // ── Nav Icons ─────────────────────────────────────────────
  const { data: iconsSetting } = useQuery<{ key: string; value: string } | null>({
    queryKey: ['settings', 'nav_icons'],
    queryFn: async () => {
      try {
        return await api.get<{ key: string; value: string }>('/settings/nav_icons');
      } catch {
        return null;
      }
    },
  });

  const [icons, setIcons] = useState<NavIconMap>({});
  useEffect(() => {
    if (iconsSetting?.value) {
      try {
        setIcons(JSON.parse(iconsSetting.value));
      } catch {
        /* ignore */
      }
    }
  }, [iconsSetting]);

  const iconsMutation = useMutation({
    mutationFn: (value: NavIconMap) =>
      api.put('/settings/nav_icons', { value: JSON.stringify(value) }),
    onSuccess: () => {
      setSaveError('');
      qc.invalidateQueries({ queryKey: ['settings', 'nav_icons'] });
    },
    onError: (err: Error) => {
      setSaveError(err.message || t('pageSettingsSaveFailed'));
    },
  });

  const updateIcon = (key: string, value: string) => {
    setIcons((prev) => ({ ...prev, [key]: value }));
  };

  const row = (item: { iconKey: string; icon: string; label: string }, indent?: boolean) => (
    <IconRow
      key={item.iconKey}
      itemKey={item.iconKey}
      defaultIcon={item.icon}
      label={item.label}
      icons={icons}
      open={openKey === item.iconKey}
      onToggle={() => setOpenKey((key) => (key === item.iconKey ? null : item.iconKey))}
      onUpdate={updateIcon}
      indent={indent}
    />
  );

  return (
    <AdminPage
      className="admin-page-body"
      header={
        <AdminPageHeader title={t('pageSettingsTitle')} description={t('pageSettingsSubtitle')} />
      }
    >
      {saveError && (
        <p role="alert" className="mb-4 border border-error p-3 text-sm text-error">
          {saveError}
        </p>
      )}

      <section
        aria-labelledby="page-settings-icons"
        className="border border-base-300 bg-base-100 p-4 sm:p-5 space-y-4"
      >
        <div>
          <h2 id="page-settings-icons" className="text-lg font-semibold text-base-content">
            {t('pageSettingsIconsLabel')}
          </h2>
          <p className="text-sm text-base-content mt-1">{t('pageSettingsIconsDesc')}</p>
        </div>

        <ul className="border-t border-base-300">
          {navItems.map((item) => (
            <Fragment key={item.iconKey}>
              {row(item)}
              {item.children?.map((child) => row(child, true))}
            </Fragment>
          ))}
        </ul>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => iconsMutation.mutate(icons)}
            disabled={iconsMutation.isPending}
            className={PRIMARY}
          >
            {iconsMutation.isPending ? t('saving') : tc('save')}
          </button>
          <button
            type="button"
            onClick={() => {
              setIcons({});
              iconsMutation.mutate({});
            }}
            className={OUTLINE}
          >
            {t('pageSettingsIconsReset')}
          </button>
          {iconsMutation.isSuccess && (
            <p role="status" className="text-sm text-base-content">
              {t('pageSettingsSaved')}
            </p>
          )}
        </div>
      </section>
    </AdminPage>
  );
}
